import React, { useEffect, useState } from 'react';
import { Checkbox, colors } from '@insurancemenu/imenu-components-lib';
import { ChevronDown, ChevronUp } from '@insurancemenu/imenu-components-lib/dist/icons';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import { smoothScrollToElement } from 'global/utils/smoothScrollToTop';
import { useParams } from 'react-router-dom';
import BatchApplyModal from '../BatchApplyModal';
import ApplyClassesModal from '../ApplyClassesModal';
import {
  ActionButtonStyled,
  ActionsButtonsWrapperStyled,
  CheckboxTextStyled,
  RelativeWrapperStyled,
  SectionContentStyled,
  SelectedAllBlockStyled,
} from './styles';
import ElectionFilter from './ElectionFilter';
import ElectionSort from './ElectionSort';
import { SUBSCRIBERS_TABLE_ID } from '../constants';

const ElectionsEmployeesHeader = observer((): JSX.Element => {
  const [isSelectButtonChecked, setSelectButtonChecked] = useState(false);
  const [isBatchApplyModalOpen, setIsBatchApplyModalOpen] = useState(false);
  const [isApplyClassesModalOpen, setIsApplyClassesModalOpen] = useState(false);

  const {
    employeeElectionsStore: {
      subscribers,
      selectedSubscribersIds,
      filter,
      activeSortValue,
      currentPage,
      isElectionCompleted,
      setAllSubscribersSelected,
      clearAllSelectedSubscribers,
      updateFilter,
      updateSortOrder,
      updateSubscribersSortBy,
      updateActiveSortValue,
    },
    subscriberStore: { isPeriodNotEditable },
    inviteModalStore: { openInviteModal },
    domainStoreV2: { showSelfEnrollFeature },
  } = useRootStore();

  const { periodId } = useParams<{ periodId: string }>();

  const convertedSubscribers = subscribers?.map((subscriber) => toJS(subscriber)).slice();
  const convertedSelectedSubscribersIds = selectedSubscribersIds?.map((id) => id).slice();

  const onSelectedEmployees = (): void => {
    if (isSelectButtonChecked) {
      clearAllSelectedSubscribers();
    } else {
      setAllSubscribersSelected();
    }
    setSelectButtonChecked(!isSelectButtonChecked);
  };

  const hideSelectedSubscribers = (): void => {
    setSelectButtonChecked(false);
    clearAllSelectedSubscribers();
  };

  useEffect(() => {
    hideSelectedSubscribers();
    smoothScrollToElement(SUBSCRIBERS_TABLE_ID);
  }, [currentPage, filter, activeSortValue]);

  useEffect(() => {
    const allSubscribersIds = convertedSubscribers.map(({ id }) => id);
    const isAllSubscribersSelected = allSubscribersIds.every((id) => convertedSelectedSubscribersIds.includes(id));

    if (!isAllSubscribersSelected) {
      setSelectButtonChecked(false);
    }
  }, [convertedSelectedSubscribersIds.length]);

  const openBatchApplyModal = (): void => {
    setIsBatchApplyModalOpen(true);
  };

  const closeBatchApplyModal = (): void => {
    setIsBatchApplyModalOpen(false);
  };

  const openApplyClassesModal = (): void => {
    setIsApplyClassesModalOpen(true);
  };

  const closeApplyClassesModal = (): void => {
    setIsApplyClassesModalOpen(false);
  };

  return (
    <SectionContentStyled>
      {isPeriodNotEditable ? (
        <ElectionSort
          activeSortValue={activeSortValue}
          updateSortOrder={updateSortOrder}
          updateSubscribersSortBy={updateSubscribersSortBy}
          updateActiveSortValue={updateActiveSortValue}
        />
      ) : (
        <ActionsButtonsWrapperStyled $showSelfEnrollFeature={showSelfEnrollFeature}>
          <SelectedAllBlockStyled>
            <Checkbox
              disabled={isPeriodNotEditable}
              label={<CheckboxTextStyled>Select All</CheckboxTextStyled>}
              checked={isSelectButtonChecked}
              onChange={onSelectedEmployees}
              highlightColor={colors.blue[600]}
            />
          </SelectedAllBlockStyled>
          <RelativeWrapperStyled>
            <ActionButtonStyled
              disabled={isPeriodNotEditable}
              rightIconProp={isBatchApplyModalOpen ? <ChevronUp /> : <ChevronDown />}
              size="large"
              color="custom"
              customColor={colors.neutral.white}
              $isSelected={isBatchApplyModalOpen}
              onClick={isBatchApplyModalOpen ? closeBatchApplyModal : openBatchApplyModal}
            >
              Batch Apply
            </ActionButtonStyled>
            <BatchApplyModal show={isBatchApplyModalOpen} onClose={closeBatchApplyModal} />
          </RelativeWrapperStyled>
          <ElectionSort
            activeSortValue={activeSortValue}
            updateSortOrder={updateSortOrder}
            updateSubscribersSortBy={updateSubscribersSortBy}
            updateActiveSortValue={updateActiveSortValue}
          />
          <ElectionFilter filter={filter} updateFilter={updateFilter} />
          {!isElectionCompleted && showSelfEnrollFeature && (
            <ActionButtonStyled
              size="large"
              color="custom"
              customColor={colors.neutral.white}
              disabled={isPeriodNotEditable}
              onClick={(): void => openInviteModal(periodId)}
            >
              Invite to Self Enroll
            </ActionButtonStyled>
          )}
          <RelativeWrapperStyled>
            <ActionButtonStyled
              size="large"
              color="custom"
              customColor={colors.neutral.white}
              $isSelected={isApplyClassesModalOpen}
              rightIconProp={isApplyClassesModalOpen ? <ChevronUp /> : <ChevronDown />}
              disabled={isPeriodNotEditable}
              onClick={isApplyClassesModalOpen ? closeApplyClassesModal : openApplyClassesModal}
            >
              Employee Classes
            </ActionButtonStyled>
            <ApplyClassesModal show={isApplyClassesModalOpen} onClose={closeApplyClassesModal} />
          </RelativeWrapperStyled>
        </ActionsButtonsWrapperStyled>
      )}
    </SectionContentStyled>
  );
});

export default ElectionsEmployeesHeader;
